<template>
	<div class="container contact-container">
		<h1 class="contact-heading">Contact</h1>
		<p>Interested in booking us for your next event?
		<br/>Complete this form and we’ll follow up to discuss.</p>

		<FormKit
			type="form"
			ref="form"
			v-model="formData"
			:form-class="submitted ? 'hidden' : 'block'"
			id="bosb-register"
			:config="{ validationVisibility: 'submit' }"
			label="Register"
			submit-label="Get in Touch"
			:actions="false"
			@submit="createCustomer"
			class="form-space-y"
		>
			<div v-if="!submitted" class="form-space-y-sm">
				<FormKit
					v-model="customer.fname"
					ref="input"
					type="text"
					name="fname"
					id="bosb-fname"
					placeholder="First Name"
					validation="required | length: 1"
					autocomplete="given-name"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.lname"
					ref="input"
					type="text"
					name="lname"
					id="bosb-lname"
					placeholder="Last Name"
					validation="required | length: 1"
					autocomplete="family-name"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.phone"
					type="tel"
					name="phone"
					id="bosb-phone"
					placeholder="Phone Number"
					validation='required | matches:/^\(\d{3}\) \d{3}-\d{4}$/'
					:validation-messages="{
						matches: 'Please enter a valid phone number in format (xxx) xxx-xxxx'
					}"
					validation-visibility="dirty"
					autocomplete="tel"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.email"
					ref="input"
					type="email"
					name="bosb-email"
					id="email"
					validation="required | email"
					placeholder="Email"
					autocomplete="email"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.eventLocation"
					type="text"
					name="eventLocation"
					id="bosb-event-location"
					placeholder="Location of Event"
					validation="required"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.eventDateTime"
					type="datetime-local"
					name="eventDateTime"
					id="bosb-event-date-time"
					label="Date and Time of Event"
					validation="required"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.soundNeeded"
					type="radio"
					name="soundNeeded"
					id="bosb-sound-needed"
					label="Is Sound Needed?"
					:options="[
					{ label: 'Yes', value: 'Yes' },
					{ label: 'No', value: 'No' }
					]"
					validation="required"
					outer-class="radio-group-inline"
				/>

				<FormKit
					v-model="customer.eventType"
					type="radio"
					name="eventType"
					id="bosb-event-type"
					label="Type of Event"
					:options="[
					{ label: 'Festival', value: 'Festival' },
					{ label: 'Wedding', value: 'Wedding' },
					{ label: 'Bar/Coffee Shop/Boutique', value: 'Bar/Coffee Shop/Boutique' },
					{ label: 'Private Party', value: 'Private Party' },
					{ label: 'Community Event', value: 'Community Event' },
					{ label: 'Other', value: 'Other' }
					]"
					validation="required"
					outer-class="radio-group-inline"
				/>

				<FormKit
					v-if="customer.eventType === 'Other'"
					v-model="customer.eventTypeOther"
					type="text"
					name="eventTypeOther"
					id="bosb-event-type-other"
					placeholder="Please specify"
					validation="required"
					input-class="input-field"
				/>

				<FormKit
					v-model="customer.message"
					type="textarea"
					name="bosb-msg"
					placeholder="Type your message..."
					rows="6"
					id="message"
					validation="required | message"
					input-class="input-field textarea-field"
				/>

				<FormKit
					type="submit"
					label="Send"
					input-class="submit-button"
				/>
			</div>
		</FormKit>

		<div v-if="submitted" class="submission-success">
			<h2>Submission successful!</h2>
			<p>Thank you for contacting us. We sent you a confirmation email. Here's what you submitted:</p>
			<ul class="submitted-data">
				<li><strong>First Name:</strong> {{ submittedData.fname }}</li>
				<li><strong>Last Name:</strong> {{ submittedData.lname }}</li>
				<li><strong>Phone:</strong> {{ submittedData.phone }}</li>
				<li><strong>Email:</strong> {{ submittedData.email }}</li>
				<li><strong>Location of Event:</strong> {{ submittedData.eventLocation }}</li>
				<li><strong>Date & Time:</strong> {{ submittedData.eventDateTime }}</li>
				<li><strong>Sound Needed:</strong> {{ submittedData.soundNeeded }}</li>
				<li>
					<strong>Type of Event:</strong>
					{{ submittedData.eventType === 'Other' ? submittedData.eventTypeOther : submittedData.eventType }}
				</li>
				<li><strong>Message:</strong> {{ submittedData.message }}</li>
			</ul>
		</div>
	</div>
</template>


<script>
import { HTTP } from '../../../api/variables'

export default {
	name: "Contact",
	data() {
		return {
			customer: {
				fname: "",
				lname: "",
				phone: "",
				email: "",
				message: "",
				eventLocation: "",
				eventDateTime: "",
				soundNeeded: "",
				eventType: "",
				eventTypeOther: ""
			},
			submitted: false,
			submittedData: null
		}
	},
	watch: {
		'customer.phone'(newVal) {
			let digits = newVal.replace(/\D/g, "").substring(0, 10);
			const area = digits.substring(0, 3);
			const middle = digits.substring(3, 6);
			const last = digits.substring(6, 10);

			let formatted = "";
			
			if (digits.length > 6) {
				formatted = `(${area}) ${middle}-${last}`;
			} else if (digits.length > 3) {
				formatted = `(${area}) ${middle}`;
			} else if (digits.length > 0) {
				formatted = `(${area}`;
			}

			if (formatted !== newVal) {
				this.customer.phone = formatted;
			}
		}
	},
	mounted() {
		window.scrollTo(0, 0);
		this.$formkit?.reset('bosb-register');
		this.$emit("adjust-margin");
	},
	methods: {
		formatPhoneNumber(value) {
			let input = value.replace(/\D/g, "").substring(0, 10);
			const areaCode = input.substring(0, 3);
			const middle = input.substring(3, 6);
			const last = input.substring(6, 10);

			if (input.length > 6) {
				input = `(${areaCode}) ${middle}-${last}`;
			} else if (input.length > 3) {
				input = `(${areaCode}) ${middle}`;
			} else if (input.length > 0) {
				input = `(${areaCode}`;
			}

			this.customer.phone = input;
		},
		createCustomer() {
			HTTP.post("contact", this.customer)
			.then((res) => {
				if (res.data.msg === "Validation Failed" || res.data.msg === undefined) {
					console.log(res.data.msg)
				} else {
					this.submitted = true
					this.submittedData = { ...this.customer }
					// this.$formkit.reset('bosb-register')
				}
			})
			.catch((err) => {
				alert(err)
			})
		}
	}
}
</script>

